import { useEffect, useRef, useState } from 'react';
import type { GameModuleProps, MemoryPair } from '../types/game';

interface Card extends MemoryPair { key: string }

function shuffledCards(pairs: MemoryPair[]): Card[] {
  return pairs.flatMap((pair) => [{ ...pair, key: `${pair.id}-a` }, { ...pair, key: `${pair.id}-b` }])
    .sort(() => Math.random() - 0.5);
}

export function MemoryModule({ module, onComplete }: GameModuleProps<'memory'>) {
  const [cards] = useState(() => shuffledCards(module.config.pairs));
  const [open, setOpen] = useState<number[]>([]);
  const [matched, setMatched] = useState<string[]>([]);
  const [attempts, setAttempts] = useState(0);
  const [locked, setLocked] = useState(false);
  const completed = useRef(false);

  useEffect(() => {
    if (!completed.current && matched.length === module.config.pairs.length) {
      completed.current = true;
      window.setTimeout(() => onComplete({ success: true, attempts }), 600);
    }
  }, [attempts, matched.length, module.config.pairs.length, onComplete]);

  const flip = (index: number) => {
    if (locked || open.includes(index) || matched.includes(cards[index]?.id ?? '')) return;
    if (open.length === 0) { setOpen([index]); return; }
    const first = open[0];
    if (first === undefined) return;
    const firstCard = cards[first];
    const nextCard = cards[index];
    if (!firstCard || !nextCard) return;
    setAttempts((value) => value + 1);
    setOpen([first, index]);
    if (firstCard.id === nextCard.id) {
      setMatched((values) => [...values, firstCard.id]);
      window.setTimeout(() => setOpen([]), 350);
    } else {
      setLocked(true);
      window.setTimeout(() => { setOpen([]); setLocked(false); }, 750);
    }
  };

  return (
    <div className="module-content">
      <p className="module-instruction">Encontrá todas las parejas.</p>
      <div className="memory-grid" style={{ '--columns': Math.min(4, module.config.pairs.length) } as React.CSSProperties}>
        {cards.map((card, index) => {
          const shown = open.includes(index) || matched.includes(card.id);
          return <button aria-label={shown ? card.label : 'Carta oculta'} className={`memory-card ${shown ? 'is-open' : ''}`}
            disabled={matched.includes(card.id)} key={card.key} onClick={() => flip(index)}>
            <span>{shown ? (card.image ? <img src={card.image} alt={card.label} /> : card.label) : '✦'}</span>
          </button>;
        })}
      </div>
      <p className="attempts">Intentos: {attempts}</p>
    </div>
  );
}
